import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * TimezoneContext
 *
 * Provides global timezone configuration for the application.
 * All dates in the system are stored in UTC and converted to the selected timezone for display.
 */

const TimezoneContext = createContext();

// Get browser's timezone
const getBrowserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Load saved timezone from localStorage
const getSavedTimezone = () => {
    try {
        const saved = localStorage.getItem('selectedTimezone');
        return saved || getBrowserTimezone();
    } catch (error) {
        console.error('Error loading timezone from localStorage:', error);
        return getBrowserTimezone();
    }
};

export const TimezoneProvider = ({ children }) => {
    const [timezone, setTimezone] = useState(getSavedTimezone);
    const [timezoneLabel, setTimezoneLabel] = useState('Browser Default');

    useEffect(() => {
        // Update label based on timezone
        if (timezone === getBrowserTimezone()) {
            setTimezoneLabel('Browser Default');
        } else {
            setTimezoneLabel(timezone);
        }
    }, [timezone]);

    // Change timezone and save to localStorage
    const changeTimezone = useCallback((newTimezone) => {
        try {
            setTimezone(newTimezone);
            localStorage.setItem('selectedTimezone', newTimezone);
        } catch (error) {
            console.error('Error saving timezone to localStorage:', error);
        }
    }, []);

    // Reset to browser timezone
    const resetToBrowserTimezone = useCallback(() => {
        const browserTz = getBrowserTimezone();
        changeTimezone(browserTz);
    }, [changeTimezone]);

    // Format a UTC date string to the selected timezone
    const formatDate = useCallback((utcDateString, options = {}) => {
        if (!utcDateString) return '';

        try {
            const date = new Date(utcDateString);

            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }

            const defaultOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: timezone,
                hour12: false
            };

            const formatOptions = { ...defaultOptions, ...options };

            return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Error formatting date';
        }
    }, [timezone]);

    // Format with custom format string
    const formatDateCustom = useCallback((utcDateString, format = 'full') => {
        if (!utcDateString) return '';

        try {
            const date = new Date(utcDateString);

            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }

            const options = {
                timeZone: timezone
            };

            switch (format) {
                case 'full':
                    return new Intl.DateTimeFormat('en-US', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                    }).format(date);

                case 'date':
                    return new Intl.DateTimeFormat('en-US', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).format(date);

                case 'time':
                    return new Intl.DateTimeFormat('en-US', {
                        ...options,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                    }).format(date);

                case 'datetime':
                    return new Intl.DateTimeFormat('en-US', {
                        ...options,
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    }).format(date);

                case 'relative':
                    // This will be handled separately
                    return formatDate(utcDateString);

                default:
                    return formatDate(utcDateString);
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Error formatting date';
        }
    }, [timezone, formatDate]);

    // Get relative time (e.g., "2 days ago")
    const getRelativeTime = useCallback((utcDateString) => {
        if (!utcDateString) return '';

        try {
            const date = new Date(utcDateString);
            const now = new Date();

            if (isNaN(date.getTime())) {
                return '';
            }

            const diffInMs = now - date;
            const diffInSeconds = Math.floor(diffInMs / 1000);
            const diffInMinutes = Math.floor(diffInSeconds / 60);
            const diffInHours = Math.floor(diffInMinutes / 60);
            const diffInDays = Math.floor(diffInHours / 24);
            const diffInYears = Math.floor(diffInDays / 365);

            if (diffInYears > 0) {
                return `(${diffInYears}yr${diffInYears > 1 ? 's' : ''})`;
            } else if (diffInDays > 0) {
                return `(${diffInDays}day${diffInDays > 1 ? 's' : ''})`;
            } else if (diffInHours > 0) {
                return `(${diffInHours}hr${diffInHours > 1 ? 's' : ''})`;
            } else if (diffInMinutes > 0) {
                return `(${diffInMinutes}min${diffInMinutes > 1 ? 's' : ''})`;
            } else {
                return '(just now)';
            }
        } catch (error) {
            console.error('Error calculating relative time:', error);
            return '';
        }
    }, []);

    // Get timezone offset string (e.g., "UTC-5" or "UTC+3")
    const getTimezoneOffset = useCallback(() => {
        try {
            const date = new Date();
            const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
            const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
            const offset = (tzDate - utcDate) / (1000 * 60 * 60);

            if (offset === 0) return 'UTC';
            const sign = offset > 0 ? '+' : '';
            return `UTC${sign}${offset}`;
        } catch (error) {
            console.error('Error getting timezone offset:', error);
            return 'UTC';
        }
    }, [timezone]);

    // Parse a datetime-local string as if it's in the selected timezone
    // Returns a Date object with the correct UTC time
    // IMPORTANT: The returned Date object represents the selected local time converted to UTC
    // When sent to API using .toISOString(), it will be in UTC regardless of user's timezone
    // Example: User selects "2025-01-15 10:00" in PST timezone
    //          Returns Date object representing "2025-01-15 18:00 UTC"
    //          API receives "2025-01-15T18:00:00.000Z" (UTC)
    const parseDateInTimezone = useCallback((datetimeLocalString) => {
        if (!datetimeLocalString) return null;

        try {
            // Parse the datetime-local string (format: "2025-11-11T14:00")
            const [datePart, timePart] = datetimeLocalString.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);

            // Start with a rough UTC date (treating input as UTC naively)
            const roughUTC = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

            // Format roughUTC in the selected timezone to find the offset
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });

            const parts = formatter.formatToParts(roughUTC);
            const f = {};
            parts.forEach(p => { if (p.type !== 'literal') f[p.type] = parseInt(p.value); });

            // Compute how many ms differ between what we want and what roughUTC shows in timezone
            const desiredMs = Date.UTC(year, month - 1, day, hours, minutes, 0);
            const formattedMs = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
            const offsetMs = desiredMs - formattedMs;

            return new Date(roughUTC.getTime() + offsetMs);
        } catch (error) {
            console.error('Error parsing date in timezone:', error);
            return null;
        }
    }, [timezone]);

    const value = {
        timezone,
        timezoneLabel,
        changeTimezone,
        resetToBrowserTimezone,
        formatDate,
        formatDateCustom,
        getRelativeTime,
        getTimezoneOffset,
        parseDateInTimezone,
        browserTimezone: getBrowserTimezone()
    };

    return (
        <TimezoneContext.Provider value={value}>
            {children}
        </TimezoneContext.Provider>
    );
};

// Custom hook to use timezone context
export const useTimezone = () => {
    const context = useContext(TimezoneContext);
    if (!context) {
        throw new Error('useTimezone must be used within a TimezoneProvider');
    }
    return context;
};

export default TimezoneContext;
